import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { EnvelopeSimple } from "@phosphor-icons/react";
import { useTurnstile } from "../auth/useTurnstile";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import { Callout } from "../ui/Callout";
import { Hint } from "../ui/Hint";
import { AuthFrame } from "./auth/AuthFrame";
import { CONTACT_EMAIL, CONTACT_MAILTO } from "../lib/contact";

/**
 * The way back in for somebody who has forgotten their password.
 *
 * `PORTAL_NO_SELF_SERVICE_RECOVERY` was the limitation this closes. Until now a client who forgot
 * their password had no path through the interface at all and Mattej set a new one for them in the
 * Supabase dashboard, which puts a person in the credential path for every reset.
 *
 * **Every outcome shows the same sentence, and that is the point rather than an oversight.**
 * plan.md section 8 requires account-enumeration-safe responses, and this form is the most tempting
 * place in the portal to break that: it would be helpful to say "we have no account with that
 * address", and saying it turns the form into an oracle that tells anyone which of Petrovic's
 * clients have portal accounts. GoTrue answers the same way for a known and an unknown address, and
 * this screen does too.
 *
 * The one thing that is said differently is a rate limit, because a 429 is a fact about this
 * browser rather than about the address. Telling somebody to wait says nothing about who exists.
 *
 * architecture.md section 4 puts Turnstile on the authentication surfaces, and password recovery has
 * been named as one of them since the first draft.
 */

export function RecoverRoute() {
  const [turnstile, turnstileMount] = useTurnstile();
  const [email, setEmail] = useState("");
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setRateLimited(false);

    try {
      const supabase = getSupabaseBrowserClient();
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        // The link's own destination comes from the email template, which points at `/auth/callback`
        // with a `token_hash`. This is the fallback GoTrue uses for a template that still carries
        // `{{ .ConfirmationURL }}`, and it has to be on the project's redirect allow-list or GoTrue
        // silently substitutes the Site URL.
        redirectTo: `${window.location.origin}/auth/callback`,
        captchaToken: turnstile.token ?? undefined
      });

      // Only a rate limit changes what is shown. Every other outcome, including an address nobody
      // has, ends on the same sentence.
      if (error?.status === 429) {
        setRateLimited(true);
        return;
      }

      setSent(true);
    } finally {
      turnstile.reset();
      setPending(false);
    }
  }

  /**
   * Drawn after `recoverPage` in `prototype/kund/pages-auth.js`, approved 2026-09-24. The sent state
   * keeps one plain line and puts the rest in a quiet one: how long the link lasts, the spam folder,
   * and the address to write to (docs/polish.md P-14), which the sketch shortened to nothing and a
   * person who never gets the mail still needs.
   */
  if (sent) {
    return (
      <AuthFrame titleId="recover-heading">
        <h2 id="recover-heading" className="k-auth-title">
          <EnvelopeSimple size={22} aria-hidden="true" />
          Kolla mejlen
        </h2>

        <p data-testid="recover-sent">Finns adressen hos oss har vi skickat en länk dit.</p>
        <p className="k-auth-note" data-testid="recover-contact">
          Länken gäller en timme och går att använda en gång. Titta i skräpposten om inget kommer,
          eller mejla Petrovic Systems på <a href={CONTACT_MAILTO}>{CONTACT_EMAIL}</a>.
        </p>

        <div className="k-auth-links">
          <Link to="/login">Tillbaka till inloggningen</Link>
        </div>
      </AuthFrame>
    );
  }

  // The lead under the heading is the heading's hint since 2026-09-24 (design.md section 3, rule 3),
  // and the field's placeholder carries the half of it a person needs while typing.
  return (
    <AuthFrame titleId="recover-heading">
      <h2 id="recover-heading" className="k-auth-title">
        Glömt lösenordet
        <Hint>Ange adressen du loggar in med, så skickar vi en länk för att välja ett nytt lösenord.</Hint>
      </h2>

      <form onSubmit={onSubmit} noValidate>
        <div className="box-field">
          <label htmlFor="recover-email">E-postadress</label>
          <input
            id="recover-email"
            name="email"
            type="email"
            autoComplete="username"
            placeholder="Adressen du loggar in med"
            required
            value={email}
            disabled={pending}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>

        {rateLimited ? (
          <Callout tone="danger" role="alert" testId="recover-rate-limited">
            För många försök på kort tid. Vänta en stund och försök igen.
          </Callout>
        ) : null}

        {turnstile.sitekey ? (
          <div ref={turnstileMount} className="cf-turnstile" data-testid="turnstile-recover" />
        ) : null}

        <button
          className="button is-primary is-block"
          type="submit"
          disabled={pending || turnstile.pending || email.trim().length === 0}
        >
          <span>
            {pending
              ? "Skickar"
              : turnstile.pending
                ? "Säkerhetskontroll pågår"
                : "Skicka länk"}
          </span>
        </button>
      </form>

      <div className="k-auth-links">
        <Link to="/login">Tillbaka till inloggningen</Link>
      </div>
    </AuthFrame>
  );
}
